import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { userService } from '../../../services/UserService';
import authService from '../../../services/AuthService';

interface UserProfileData {
  id: number;
  uid: string;
  name: string;
  middle_name?: string;
  last_name?: string;
  email: string;
  picture?: string;
  role: string;
}

interface UserProfilePanelProps {
  showNotification: (title: string, message: string, type: 'success' | 'error' | 'info') => void;
}

const UserProfilePanel: React.FC<UserProfilePanelProps> = ({ showNotification }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    middle_name: '',
    last_name: '',
    email: '',
    picture: ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await userService.getUserProfile();
      const profileData = response.data;
      setProfile(profileData);
      setFormData({
        name: profileData.name || '',
        middle_name: profileData.middle_name || '',
        last_name: profileData.last_name || '',
        email: profileData.email || '',
        picture: profileData.picture || ''
      });
    } catch (error: any) {
      console.error('Failed to load profile:', error);
      showNotification(
        t('profile.error', 'Error'),
        t('profile.loadError', 'Failed to load profile information'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Prepare update data (only send changed fields)
      const updateData: any = {};
      if (formData.name !== profile?.name) {
        updateData.name = formData.name;
      }
      if (formData.middle_name !== (profile?.middle_name || '')) {
        updateData.middle_name = formData.middle_name;
      }
      if (formData.last_name !== (profile?.last_name || '')) {
        updateData.last_name = formData.last_name;
      }
      if (formData.email !== profile?.email) {
        updateData.email = formData.email;
      }
      if (formData.picture !== profile?.picture) {
        updateData.picture = formData.picture;
      }

      const response = await userService.updateUserProfile(updateData);
      const updatedProfile = response.data;

      setProfile(updatedProfile);
      setFormData({
        name: updatedProfile.name || '',
        middle_name: updatedProfile.middle_name || '',
        last_name: updatedProfile.last_name || '',
        email: updatedProfile.email || '',
        picture: updatedProfile.picture || ''
      });
      setHasChanges(false);

      showNotification(
        t('profile.success', 'Success'),
        t('profile.updateSuccess', 'Profile updated successfully'),
        'success'
      );
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      const errorMessage = error?.response?.data?.detail || t('profile.updateError', 'Failed to update profile');
      showNotification(
        t('profile.error', 'Error'),
        errorMessage,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        middle_name: profile.middle_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        picture: profile.picture || ''
      });
      setHasChanges(false);
    }
  };

  const handlePasswordInputChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showNotification(
        t('profile.error', 'Error'),
        t('profile.passwordMismatch', 'New passwords do not match'),
        'error'
      );
      return;
    }
    if (passwordData.newPassword.length < 6) {
      showNotification(
        t('profile.error', 'Error'),
        t('profile.passwordTooShort', 'Password must be at least 6 characters'),
        'error'
      );
      return;
    }
    try {
      setChangingPassword(true);
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
      showNotification(
        t('profile.success', 'Success'),
        t('profile.passwordChanged', 'Password changed successfully'),
        'success'
      );
    } catch (error: any) {
      const code = error?.code;
      let message = t('profile.passwordChangeError', 'Failed to change password');
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        message = t('profile.wrongPassword', 'Current password is incorrect');
      } else if (code === 'auth/weak-password') {
        message = t('profile.weakPassword', 'Password is too weak');
      }
      showNotification(t('profile.error', 'Error'), message, 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t('profile.title', 'Profile')}</h2>
        <p className="text-sm text-gray-600">{t('profile.subtitle', 'Manage your account settings and preferences')}</p>
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        {/* First Name Field */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('profile.firstName', 'First name')}
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder={t('profile.firstNamePlaceholder', 'Enter your first name')}
          />
        </div>

        {/* Middle Name Field */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('profile.middleName', 'Middle name')}
          </label>
          <input
            type="text"
            value={formData.middle_name}
            onChange={(e) => handleInputChange('middle_name', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder={t('profile.middleNamePlaceholder', 'Enter your middle name')}
          />
        </div>

        {/* Last Name Field */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('profile.lastName', 'Last name')}
          </label>
          <input
            type="text"
            value={formData.last_name}
            onChange={(e) => handleInputChange('last_name', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder={t('profile.lastNamePlaceholder', 'Enter your last name')}
          />
        </div>

        {/* Email Field */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            {t('profile.email', 'Email')}
          </label>
          <input
            disabled
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder={t('profile.emailPlaceholder', 'Enter your email')}
          />
        </div>
      </div>

      {/* Account Info */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-4">
          {t('profile.accountInfo', 'Account information')}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-600">{t('profile.userId', 'User ID')}</span>
            <span className="text-sm font-mono text-gray-900">{profile?.uid}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-600">{t('profile.role', 'Role')}</span>
            <span className="text-sm text-gray-900 capitalize">{profile?.role}</span>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-900">
            {t('profile.changePassword', 'Change Password')}
          </h3>
          <button
            onClick={() => {
              setShowPasswordForm(v => !v);
              setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            }}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-2 transition-colors"
          >
            {showPasswordForm ? t('profile.cancel', 'Cancel') : t('profile.changePasswordBtn', 'Change Password')}
          </button>
        </div>
        {showPasswordForm && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('profile.currentPassword', 'Current Password')}
              </label>
              <input
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder={t('profile.currentPasswordPlaceholder', 'Enter current password')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('profile.newPassword', 'New Password')}
              </label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder={t('profile.newPasswordPlaceholder', 'Enter new password')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('profile.confirmPassword', 'Confirm New Password')}
              </label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder={t('profile.confirmPasswordPlaceholder', 'Confirm new password')}
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {changingPassword ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>{t('profile.changingPassword', 'Changing...')}</span>
                  </>
                ) : (
                  <span>{t('profile.changePasswordBtn', 'Change Password')}</span>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {hasChanges && (
        <div className="mt-8 flex items-center justify-end gap-3">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {t('profile.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>{t('profile.saving', 'Saving...')}</span>
              </>
            ) : (
              <span>{t('profile.save', 'Save')}</span>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfilePanel;
